import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, RefreshCcw, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, digits = 2) =>
  n == null ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

function QuoteRow({ q, compact = false }) {
  const up = (q.change_pct ?? 0) >= 0;
  const color = up ? "text-emerald-600" : "text-red-600";
  return (
    <div className={`flex items-center justify-between gap-3 ${compact ? "py-1.5" : "py-2.5"} border-b border-[var(--line)] last:border-0`} data-testid={`quote-${q.symbol}`}>
      <div className="min-w-0">
        <div className="text-[13px] font-black tracking-tight text-[var(--ink)] truncate">{q.label || q.symbol}</div>
        <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">{q.symbol}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[14px] font-black tabular-nums text-[var(--ink)]">{fmt(q.price, q.price > 1000 ? 0 : 2)}</div>
        <div className={`text-[11px] font-bold tabular-nums ${color} flex items-center justify-end gap-0.5`}>
          {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
          {up ? "+" : ""}{fmt(q.change_pct, 2)}%
        </div>
      </div>
    </div>
  );
}

function Ticker({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="bg-[#0a1f3d] text-white overflow-hidden border-y border-[var(--gold)]" data-testid="markets-ticker">
      <div className="marquee-track py-2 px-3">
        {[...rows, ...rows].map((q, i) => {
          const up = (q.change_pct ?? 0) >= 0;
          return (
            <span key={i} className="text-[12px] font-bold tracking-tight tabular-nums flex items-center gap-1.5 mr-8 whitespace-nowrap">
              <span className="text-[var(--gold)]">{q.label || q.symbol}</span>
              <span>{fmt(q.price, q.price > 1000 ? 0 : 2)}</span>
              <span className={up ? "text-emerald-300" : "text-red-300"}>
                {up ? "▲" : "▼"} {fmt(Math.abs(q.change_pct), 2)}%
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default function MarketsBoard({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/markets/quotes`, { timeout: 25000 });
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // 5-minute refresh (was 60s — was causing "blinking" complaints because
    // the ticker re-rendered every minute on the home page).
    const id = setInterval(load, 5 * 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-md border border-[var(--line)] p-6 flex items-center gap-3 justify-center" data-testid="markets-loading">
        <Loader2 className="animate-spin text-[var(--blue)]" size={20} />
        <span className="text-[13px] text-[var(--muted)]">Loading live markets…</span>
      </div>
    );
  }
  if (!data) return null;

  const groups = [
    { key: "indices", label: "Indices", testid: "panel-indices" },
    { key: "mega_caps", label: "Mega Caps", testid: "panel-mega-caps" },
    { key: "commodities", label: "Commodities", testid: "panel-commodities" },
    { key: "treasuries", label: "Treasuries", testid: "panel-treasuries" },
    { key: "currencies", label: "FX + Crypto", testid: "panel-currencies" },
    { key: "crypto", label: "Crypto Top 10", testid: "panel-crypto" },
  ];

  if (compact) {
    return (
      <div className="bg-white rounded-md border border-[var(--line)] p-3" data-testid="markets-board-compact">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--blue)]">Markets · Live</span>
          <button onClick={load} className="text-[var(--muted)] hover:text-[var(--blue)]" aria-label="refresh" data-testid="markets-refresh-compact">
            <RefreshCcw size={11} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
        {(data.indices || []).map((q) => <QuoteRow key={q.symbol} q={q} compact />)}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="markets-board">
      <Ticker rows={[...(data.indices || []), ...(data.mega_caps || []).slice(0, 5), ...(data.commodities || []).slice(0, 5)]} />
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--blue)]">
          Live Markets · refreshes every 60s · as of {new Date(data.as_of).toLocaleTimeString()}
        </div>
        <button onClick={load} className="btn icon" data-testid="markets-refresh">
          <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {groups.map((g) => (
          <div key={g.key} className="bg-white rounded-md border border-[var(--line)] p-3" data-testid={g.testid}>
            <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)] mb-1">{g.label}</div>
            {(data[g.key] || []).slice(0, 10).map((q) => <QuoteRow key={q.symbol || q.id} q={q} />)}
            {(data[g.key] || []).length === 0 && (
              <div className="text-[12px] text-[var(--muted)] italic py-2">No data — try refresh.</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
